/**
 * URL-level venue access.
 *
 * Hiding a venue from the switcher stops someone stumbling into it; it does
 * nothing about a bookmark, a shared link, or a hand-edited serial. This is the
 * other half.
 *
 * It is a BOUNDARY mounted once around the org outlet, not a per-route flag.
 * The earlier version needed `requiresVenueAccess: true` on the `:serial` route,
 * which is one more rule someone has to remember to attach — the same failure
 * mode as `deniedRoles` (declared, never set) and the four nav sections that
 * rendered for Front of House because nobody put a `capability` on them. A
 * boundary cannot be forgotten: it reads the serial out of the URL, so every
 * venue route present or future is covered by construction, and it no-ops on
 * org-level routes that have no serial.
 *
 * ⚠️ Still client-side. A determined user can call the API directly and it will
 * answer. Backend enforcement is separate, deliberately-deferred work.
 */
import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { Button } from 'tailwind-v4/components/button'
import useLocations from '@/state/entities/locations/hooks/useLocation'
import { useAccess } from './access.provider'
import { canAccessSerial, firstAccessibleSerial } from './venue-access'

/**
 * Venue serial from the URL. Venue routes are always
 * `/:orgId/venues/:serial/…`, so the serial is at index 2 behind `venues` — or
 * absent, which is every org-level route.
 */
function parseSerial(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean)
  if (segments[1] !== 'venues') return undefined
  const serial = segments[2]
  return serial && serial.length > 0 ? serial : undefined
}

/**
 * Enforce venue access for whatever is in the URL.
 *
 * Mount once around the org `<Outlet />`. Renders `children` untouched when
 * there is no serial in the path, when the user is unrestricted, or when the
 * serial is one they hold.
 */
export function VenueAccessBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const { subject } = useAccess()
  // Already access-filtered, so this IS the set of venues they may reach. Typed
  // narrowly at the boundary because `organisation.locations` is untyped upstream
  // — the serial is all this needs.
  const venues: { serial?: string | null }[] = useLocations()

  const serial = parseSerial(pathname)

  // No venue in the URL — an org-level route. Nothing to enforce.
  if (!serial) return <>{children}</>

  // `accessibleSerials` cannot fail closed on its own: an unloaded user and an
  // unrestricted one both read as "no restriction". So wait for the subject
  // before redirecting anyone.
  //
  // Children render during that window rather than a spinner. This sits above
  // EVERY org page, and blocking all of them on the role query would put a
  // flash of spinner on every cold load. The cost is that a denied page may
  // mount for one render and fire its queries — acceptable while the API is
  // unguarded anyway, and the redirect lands before anything is shown.
  if (!subject.isResolved) return <>{children}</>

  if (canAccessSerial(subject.accessibleSerials, serial)) {
    return <>{children}</>
  }

  // Send them to a venue they CAN reach, preserving the rest of the path so a
  // shared "day planner" link still lands on the day planner. This fires on
  // stale bookmarks as much as on anything untoward, so a redirect beats an
  // error page.
  const fallback = firstAccessibleSerial(venues, subject.accessibleSerials)
  if (fallback) {
    return (
      <Navigate
        to={pathname.replace(`/venues/${serial}`, `/venues/${fallback}`)}
        replace
      />
    )
  }

  return <NoVenueAccess />
}

/**
 * Shown when the user has access to no venue at all — a real state for a
 * freshly-invited member whose venues have not been assigned yet.
 */
function NoVenueAccess() {
  return (
    <div className="new-ui-theme flex h-full w-full items-center justify-center bg-background p-8 text-foreground">
      <div className="flex w-full max-w-md flex-col items-center gap-6 text-center">
        <div className="flex flex-col gap-2">
          <h1 className="text-lg font-semibold">No venue access</h1>
          <p className="text-sm text-muted-foreground">
            You don&apos;t have access to this venue. Ask an administrator in
            your organisation to give you access to the venues you need.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            window.location.assign('/')
          }}
        >
          Go back
        </Button>
      </div>
    </div>
  )
}
